import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { get } from 'lodash';
import {
	reduxForm,
	Field,
	SubmissionError,
	FieldArray,
	formValueSelector,
} from 'redux-form';
import { withRouter } from 'react-router-dom';
import { compose, withProps, withHandlers, withState } from 'recompose';
import {
	createValidator,
	required,
	email,
	phone as phoneValidation,
} from '@sparefoot/react-core/utils/validation';
import {
	Form,
	FormGroup,
	FormItem,
	ReduxFormInput,
	ReduxCAPTCHA,
	ReduxFormFieldArrayV2,
} from 'components/core/Form';
import { UnitReservationFormSelector } from 'store/selectors/unit';

import warningIcon from 'assets/images/icons/warning-icon.svg';
import { Group } from 'components/core/Page';
import { setLocation } from 'utils/helpers';
import { Button } from 'components/core/Button';
import { Link } from 'components/core/Link';
import { LoadingMask } from 'components/core/LoadingMask';
import { Paragraph } from 'components/core/Typography';
import { Alert } from 'components/core/Alert';
import { Image } from 'components/core/Image';
import { CalendarV2 } from 'components/core/CalendarV2';
import './UnitReservationFormV2.scss';
import { setCookie } from '../../../utils/localStorage/localStorage';
import { trackCTAClick } from '../../core/library/tracking-utils/tracking/clickTrackingUtils';

const validate = createValidator({
	firstName: [required],
	lastName: [required],
	email: [required, email],
	phoneNumber: [required, phoneValidation],
	moveInDate: [required],
});

const formSelector = formValueSelector('unit-reservation');

export const enhance = compose(
	withRouter,
	withProps(({ facility }) => ({
		closures: get(facility, 'closures', []).filter(Boolean),
		showFeeWarning: get(facility, 'showFeeWarning', false),
		facilityCityUrl: facility?.cityUrl,
	})),
	connect((state, ownProps) => ({
		...UnitReservationFormSelector(state, ownProps),
		moveInDate: formSelector(state, 'moveInDate'),
	})),
	withState('lastSubmission', 'setLastSubmission', null),
	withState('botErrors', 'setBotErrors', false),
	withHandlers({
		// onSubmit tag on the facility and unit for the ride
		onSubmit:
			(props) =>
			({ alternateNames = [], ...formData }) => {
				const noChangeSinceLastSubmit =
					props.lastSubmission &&
					JSON.stringify(props.lastSubmission) ===
						JSON.stringify(formData);
				if (noChangeSinceLastSubmit) {
					throw new SubmissionError({ _error: 'ReSubmissionError' });
				} else {
					props.setLastSubmission(formData);
					let click_url = props?.unit?.url || null;

					trackCTAClick(
						'complete-reservation',
						'Complete Reservation Button Clicked',
						'Reserve Now to Lock in Rate',
						click_url,
					);
					return props
						.onSubmit({
							...formData,

							facilityId: props.facility.id,
							unitId: props.unit.id,
							alternateFirstNames: alternateNames.map(
								(name) => name.firstName,
							),
							alternateLastNames: alternateNames.map(
								(name) => name.lastName,
							),
						})
						.then(({ error, payload }) => {
							if (error) {
								props.setBotErrors(
									error ===
										"Please prove you're not a robot." ||
										payload.data?.error?.errors[0]
											?.user_message ===
											`Please prove you're not a robot.`,
								);
								throw new SubmissionError({ _error: error });
							}

							const hasAuth =
								payload.entities.bookings[payload.result]
									.bookingAuth;

							// Redirect to confirmation page
							const redirectionConfirmationCode = hasAuth
								? `/reservation/confirmation/${payload.result}?email=${formData.email}`
								: `/reservation/confirmation/${payload.result}`;

							if (
								payload?.entities.bookings[payload.result]
									.bookingAuth
							) {
								setCookie(
									'jwt',
									payload.entities.bookings[payload.result]
										.bookingAuth,
								);
							}

							setLocation(redirectionConfirmationCode, 1000);

							return {
								payload,
							};
						});
				}
			},
	}),
	reduxForm({
		form: 'unit-reservation',
		destroyOnUnmount: false,
		forceUnregisterOnUnmount: true,
		validate,
		onChange: (values, dispatch, props) => {
			// Clear submission errors when form values change
			if (props.error) {
				props.clearSubmitErrors();
			}
		},
	}),
);

export function UnitReservationFormV2({
	botErrors,
	error,
	handleSubmit,
	closures,
	invalid,
	recaptchaSiteKey,
	submitting,
	unit,
	facilityCityUrl,
	moveInDate,
}) {
	return (
		<div className="unit-reservation-form-v2">
			<Form
				className="reservation-form"
				onSubmit={handleSubmit}
				invalid={invalid}
				successLabel="success-web"
				failureLabel="fail-web"
			>
				<div className="form-container">
					<div className="form-fields">
						<div className="form-row-container">
							<FormGroup>
								<FormItem>
									<Field
										name="firstName"
										id="firstName"
										label="First Name"
										placeholder="Enter First Name"
										component={ReduxFormInput}
										errorMessage="Please enter a first name"
									/>
								</FormItem>
							</FormGroup>
							<FormGroup>
								<FormItem>
									<Field
										name="lastName"
										id="lastName"
										label="Last Name"
										placeholder="Enter Last Name"
										component={ReduxFormInput}
										errorMessage="Please enter a last name"
									/>
								</FormItem>
							</FormGroup>
						</div>
						<div className="form-row-container">
							<FormGroup>
								<FormItem>
									<Field
										name="email"
										id="email"
										label="Email Address"
										type="email"
										placeholder="Enter Email"
										component={ReduxFormInput}
										errorMessage="Please provide a valid email address"
									/>
								</FormItem>
							</FormGroup>
							<FormGroup>
								<FormItem>
									<Field
										type="tel"
										name="phoneNumber"
										id="phoneNumber"
										label="Phone Number"
										placeholder="Ex: 123-123-1234"
										component={ReduxFormInput}
										helpText="So that we can validate your reservation"
										errorMessage="Please provide a valid phone number"
									/>
								</FormItem>
							</FormGroup>
						</div>
						<Group>
							<FieldArray
								className="alternate-names"
								name="alternateNames"
								label="Add another name to this reservation"
								heading="Additional User"
								array={[
									{
										name: 'firstName',
										label: 'Alternate First Name',
										placeholder: 'Alternate First Name',
										component: ReduxFormInput,
									},
									{
										name: 'lastName',
										label: 'Alternate Last Name',
										placeholder: 'Alternate Last Name',
										component: ReduxFormInput,
									},
								]}
								component={ReduxFormFieldArrayV2}
							/>
						</Group>
					</div>
				</div>
				<FormGroup className="calendar-group">
					<FormItem className="form-calendar">
						<Field
							name="moveInDate"
							component={CalendarV2}
							closures={closures}
							reservationWindow={unit.reservationWindow}
							moveInDate={moveInDate}
						/>
					</FormItem>
				</FormGroup>
				{!!botErrors && (
					<FormGroup className="captcha-group">
						<FormItem>
							<Field
								name="recaptchaToken"
								sitekey={recaptchaSiteKey}
								component={ReduxCAPTCHA}
							/>
						</FormItem>
					</FormGroup>
				)}
				{!botErrors && (
					<Field
						name="recaptchaToken"
						type="hidden"
						component="input"
					/>
				)}
				{!!error && !submitting && !botErrors && (
					<Alert
						type="warning"
						className="main-alert-container"
					>
						<Image
							src={warningIcon}
							alt="warning-icon"
						/>
						<div>
							<Paragraph
								weight="extraBold"
								last
							>
								{'Your reservation had an error. '}
							</Paragraph>
							<Paragraph last>
								{
									'Please check the information you provided and make a change to resubmit your reservation.'
								}
								{error !== 'ReSubmissionError' && (
									<>
										<br /> &nbsp;&#8226; {error}{' '}
									</>
								)}
							</Paragraph>
						</div>
					</Alert>
				)}
				<div className="reservation-button-container">
					<Group className="button-container">
						<Button
							primary
							fullWidth
							type="submit"
							disabled={submitting || invalid || !!error}
							className={classnames('submit-button', {
								'disabled-submit-button':
									submitting || invalid || !!error,
							})}
							// onClick={handleTrackCTA}
						>
							{'Reserve Now, Pay Later'}
						</Button>
					</Group>
					{!!error && !submitting && !botErrors && (
						<Alert type="warning">
							<Paragraph
								className="alert-with-suggestion"
								last
							>
								{
									'If you continue to experience errors with this reservation, you may need to come back and try again at a later time or'
								}
								&nbsp;
								<Link
									underline
									href={facilityCityUrl}
									rel="noopener noreferrer"
								>
									try exploring other storage options in the
									same area if you are looking to reserve
									storage right now
								</Link>{' '}
								<span>
									or call{' '}
									<a href="tel:8005555555">
										(800) 555 - 5555
									</a>{' '}
									to get help in finding the best storage
									option for you.
								</span>
							</Paragraph>
						</Alert>
					)}
					<Group className="disclaimer">
						<p className="disclaimer-info-head">
							You will not be charged for a reservation until
							move-in
						</p>
						<p className="disclaimer-info-label">
							No obligation to rent. By reserving this unit, you
							agree to our{' '}
							<Link
								className="disclaimer-link"
								href="/legal/consumer"
								target="_blank"
								rel="noopener noreferrer"
							>
								Terms & Services
							</Link>{' '}
							and our{' '}
							<Link
								className="disclaimer-link"
								href="/legal/privacy"
								target="_blank"
								rel="noopener noreferrer"
							>
								Privacy Policy
							</Link>
							.
						</p>
					</Group>
				</div>
			</Form>
			{submitting && (
				<LoadingMask
					modal
					message="Please wait while we reserve your unit…"
				/>
			)}
		</div>
	);
}

UnitReservationFormV2.propTypes = {
	botErrors: PropTypes.bool,
	error: PropTypes.string,
	handleSubmit: PropTypes.func.isRequired,
	recaptchaSiteKey: PropTypes.string.isRequired,
	unit: PropTypes.object.isRequired,
	invalid: PropTypes.bool,
	submitting: PropTypes.bool,
	closures: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
	facilityCityUrl: PropTypes.string,
	showFeeWarning: PropTypes.bool,
	facility: PropTypes.object,
	moveInDate: PropTypes.string,
};

export default enhance(UnitReservationFormV2);
